import { useEffect } from 'react';

function upsert(selector, create) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = create();
    element.setAttribute('data-blog-meta', '1');
    document.head.appendChild(element);
  }
  return element;
}

function setContent(element, content) {
  const previous = element.getAttribute('content');
  element.setAttribute('content', content || '');
  return previous;
}

export function siteOrigin() {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return window.location.origin;
  }
  return 'https://myblrc.in';
}

export function absoluteHttps(url) {
  const value = String(url || '').trim();
  if (!value) return 'https://myblrc.in/favicon.png';
  if (/^https:\/\//i.test(value)) return value;
  if (/^http:\/\//i.test(value)) return `https://${value.slice(7)}`;
  if (value.startsWith('//')) return `https:${value}`;
  if (value.startsWith('uploads/blogs/')) return `https://myblrc.in/api/${value.replace(/^\//, '')}`;
  return `https://myblrc.in/${value.replace(/^\//, '')}`;
}

export function usePageMeta({ title, description, canonical, image, imageAlt, jsonLd, prev, next, type = 'article' }) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const descriptionEl = upsert('meta[name="description"]', () => {
      const element = document.createElement('meta');
      element.setAttribute('name', 'description');
      return element;
    });
    const previousDescription = setContent(descriptionEl, description);

    const canonicalEl = upsert('link[rel="canonical"]', () => {
      const element = document.createElement('link');
      element.setAttribute('rel', 'canonical');
      return element;
    });
    const previousCanonical = canonicalEl.getAttribute('href');
    if (canonical) canonicalEl.setAttribute('href', canonical);

    const og = (property, content) => {
      const element = upsert(`meta[property="${property}"]`, () => {
        const node = document.createElement('meta');
        node.setAttribute('property', property);
        return node;
      });
      return { element, previous: setContent(element, content) };
    };
    const shareImage = absoluteHttps(image);
    const tags = [
      og('og:site_name', 'BLRC'),
      og('og:title', title),
      og('og:description', description),
      og('og:url', canonical || window.location.href),
      og('og:type', type),
      og('og:image', shareImage),
      og('og:image:secure_url', shareImage),
      og('og:image:alt', imageAlt || title),
    ];
    const twitter = (name, content) => {
      const element = upsert(`meta[name="${name}"]`, () => {
        const node = document.createElement('meta');
        node.setAttribute('name', name);
        return node;
      });
      return { element, previous: setContent(element, content) };
    };
    tags.push(
      twitter('twitter:card', 'summary_large_image'),
      twitter('twitter:title', title),
      twitter('twitter:description', description),
      twitter('twitter:image', shareImage),
      twitter('twitter:image:alt', imageAlt || title),
    );

    const linkRel = (rel, href) => {
      const element = upsert(`link[rel="${rel}"][data-blog-meta="1"]`, () => {
        const node = document.createElement('link');
        node.setAttribute('rel', rel);
        return node;
      });
      const previous = element.getAttribute('href');
      if (href) element.setAttribute('href', href);
      else element.removeAttribute('href');
      return { element, previous };
    };
    const prevLink = linkRel('prev', prev);
    const nextLink = linkRel('next', next);

    let script = document.getElementById('blog-jsonld');
    const hadScript = Boolean(script);
    const previousJson = script ? script.textContent : '';
    if (jsonLd) {
      if (!script) {
        script = document.createElement('script');
        script.id = 'blog-jsonld';
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = jsonLd;
    }

    return () => {
      document.title = previousTitle;
      if (previousDescription == null) descriptionEl.removeAttribute('content');
      else descriptionEl.setAttribute('content', previousDescription);
      if (previousCanonical == null) canonicalEl.removeAttribute('href');
      else canonicalEl.setAttribute('href', previousCanonical);
      tags.forEach((item) => {
        if (item.previous == null) item.element.removeAttribute('content');
        else item.element.setAttribute('content', item.previous);
      });
      [prevLink, nextLink].forEach((item) => {
        if (item.previous == null) item.element.removeAttribute('href');
        else item.element.setAttribute('href', item.previous);
      });
      if (script) {
        if (!hadScript) script.remove();
        else script.textContent = previousJson;
      }
    };
  }, [title, description, canonical, image, imageAlt, jsonLd, prev, next, type]);
}
